import { useEffect, useState } from 'react';
import type { ProvisioningAction, ProvisioningCommand } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import {
  fetchProvisioningCommands,
  queueProvisioningCommand,
  fetchOrganisationsList,
} from '../lib/queries-commercial.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  StatusPill,
  Modal,
  Input,
  Select,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  PermissionDeniedState,
  useToast,
} from '../components/ui/index.js';

export function ProvisioningPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const allowed = hasPermission('provisioning.read');
  const [commands, setCommands] = useState<ProvisioningCommand[]>([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCommand, setSelectedCommand] = useState<ProvisioningCommand | null>(null);
  const [showDispatchModal, setShowDispatchModal] = useState(false);

  // Form states
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [installationId, setInstallationId] = useState('inst-cauvery-cloud-01');
  const [action, setAction] = useState<string>('sync_entitlements');
  const [reason, setReason] = useState('Manual synchronization of active modules');

  const loadData = async () => {
    try {
      setLoading(true);
      const [cmdData, orgsData] = await Promise.all([
        fetchProvisioningCommands(),
        fetchOrganisationsList(),
      ]);
      setCommands(cmdData.items);
      setOrganisations(orgsData);
      if (orgsData.length > 0) {
        setSelectedOrgId(orgsData[0]!.id);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) {
      void loadData();
    }
  }, [allowed]);

  if (!allowed) return <PermissionDeniedState what="Provisioning Center" />;

  const handleDispatch = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await queueProvisioningCommand({
        organisationId: selectedOrgId,
        installationId,
        action: action as ProvisioningAction,
        payload: {
          timestamp: new Date().toISOString(),
          requestedBy: 'operator',
        },
        reason,
      });
      setShowDispatchModal(false);
      await loadData();
    } catch (err) {
      toast.error(`Command queueing failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="provisioning-page">
      <PageHeader
        title="Provisioning & Connector Outbox"
        subtitle="Cryptographically signed outbox queue for remote hospital installation provisioning"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Provisioning' }]} />
        }
        actions={
          hasPermission('provisioning.dispatch') ? (
            <Button
              variant="primary"
              onClick={() => setShowDispatchModal(true)}
              data-testid="dispatch-command-button"
            >
              + Dispatch Command
            </Button>
          ) : undefined
        }
      />

      {error && <ErrorState description={error} onRetry={loadData} />}

      {/* Outbox Commands Table */}
      <TableContainer>
        <Table data-testid="commands-table">
          <THead>
            <TR>
              <TH>Action</TH>
              <TH>Organisation</TH>
              <TH>Installation ID</TH>
              <TH>Signature (HMAC)</TH>
              <TH>Attempts</TH>
              <TH>Status</TH>
              <TH>Action</TH>
            </TR>
          </THead>
          <TBody>
            {loading ? (
              <TR>
                <TD colSpan={7} className="text-center py-6">
                  <LoadingState label="Loading commands…" />
                </TD>
              </TR>
            ) : commands.length === 0 ? (
              <TR>
                <TD colSpan={7} className="text-center text-muted-foreground py-6">
                  No provisioning commands queued
                </TD>
              </TR>
            ) : (
              commands.map((cmd) => (
                <TR
                  key={cmd.id}
                  onClick={() => setSelectedCommand(cmd)}
                  className="cursor-pointer hover:bg-surface-raised transition-colors"
                >
                  <TD className="font-semibold text-foreground">
                    <code>{cmd.action}</code>
                  </TD>
                  <TD>{cmd.organisationName ?? cmd.organisationId}</TD>
                  <TD className="font-mono text-xs">{cmd.installationId}</TD>
                  <TD className="font-mono text-xs text-muted-foreground">
                    {cmd.signedSignature.slice(0, 10)}...
                  </TD>
                  <TD className="text-xs">
                    {cmd.attempts} / {cmd.maxAttempts}
                  </TD>
                  <TD>
                    <StatusPill
                      tone={
                        cmd.status === 'acknowledged'
                          ? 'success'
                          : cmd.status === 'dispatched'
                            ? 'info'
                            : cmd.status === 'queued'
                              ? 'warning'
                              : 'error'
                      }
                      label={cmd.status}
                    />
                  </TD>
                  <TD onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCommand(cmd)}
                      data-testid={`view-cmd-${cmd.id}`}
                    >
                      View
                    </Button>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>

      {/* Command Details Drawer */}
      {selectedCommand && (
        <Card data-testid="command-detail-drawer" className="space-y-4">
          <CardHeader
            title={`Provisioning Command: ${selectedCommand.action}`}
            actions={
              <Button variant="ghost" size="sm" onClick={() => setSelectedCommand(null)}>
                Close
              </Button>
            }
          />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground block">Organisation</span>
                <span className="font-medium text-foreground">
                  {selectedCommand.organisationName}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Installation</span>
                <span className="font-mono text-foreground">{selectedCommand.installationId}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">Idempotency Key</span>
                <span className="font-mono text-foreground">{selectedCommand.idempotencyKey}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">Status</span>
                <span className="font-semibold text-foreground">{selectedCommand.status}</span>
              </div>
            </div>

            <div>
              <span className="text-xs text-muted-foreground block mb-1">HMAC Signature</span>
              <div className="font-mono text-xs p-2 bg-surface-raised rounded break-all border border-border">
                {selectedCommand.signedSignature}
              </div>
            </div>

            <div>
              <span className="text-xs text-muted-foreground block mb-1">Payload</span>
              <pre className="text-xs p-3 bg-surface-raised rounded overflow-x-auto border border-border">
                {JSON.stringify(selectedCommand.payload, null, 2)}
              </pre>
            </div>

            {selectedCommand.ackPayload && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  Acknowledgement Response
                </span>
                <pre className="text-xs p-3 bg-surface-raised text-foreground rounded overflow-x-auto border border-border">
                  {JSON.stringify(selectedCommand.ackPayload, null, 2)}
                </pre>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Dispatch Modal */}
      {showDispatchModal && (
        <Modal
          open={showDispatchModal}
          title="Enqueue Provisioning Command"
          onClose={() => setShowDispatchModal(false)}
        >
          <form onSubmit={handleDispatch} className="space-y-3">
            <Select
              label="Organisation"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              required
            >
              {organisations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.displayName}
                </option>
              ))}
            </Select>

            <Input
              label="Installation ID"
              value={installationId}
              onChange={(e) => setInstallationId(e.target.value)}
              required
            />

            <Select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="sync_entitlements">sync_entitlements</option>
              <option value="enable_module">enable_module</option>
              <option value="disable_module">disable_module</option>
              <option value="update_capacity_limits">update_capacity_limits</option>
              <option value="apply_license_key">apply_license_key</option>
            </Select>

            <Input
              label="Dispatch Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowDispatchModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Sign & Enqueue
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
